import { useEffect, useMemo, useState } from 'react';

import useCustomExcelFileName from 'hooks/useCustomExcelFileName';
import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useEventWithDimensions from 'hooks/useEventWithDimensions';

import {
    arrayBufferToString,
    generateTabs,
} from 'components/Dashboards/Project/Components/VersionCompare/utils/helper';
import { maxPageSize, smallPageSize } from 'components/Dashboards/Project/Components/VersionCompare/utils/table.utils';

export const useStartDateChangesLogic = ({ api, ui, title, subTitle, isSinglePage, table }) => {
    const [spinnerOpen, setSpinnerOpen] = useState<boolean>(false);
    const [pageSize, setPageSize] = useState<number>(smallPageSize);
    const finalPageSize = useMemo(() => (isSinglePage ? maxPageSize : pageSize), [pageSize, isSinglePage]);

    const reportType = api.getStartDateChanges.data?.typeId || '';
    const fileName = useCustomExcelFileName({
        projectId: ui.state.projectId,
        contractId: ui.state.contractId,
        latestVersionId: ui.state.latestVersionId,
        title,
        prefix: 'Change_Analysis_',
    });

    const { handleSnackBar } = useCustomSnackBar();
    const { sendEventWithDimensions } = useEventWithDimensions();

    const handleAPIExcelExport = () => {
        setSpinnerOpen(true);

        api.exportCsvReport.instance.mutate(api.exportCsvReport.createPayload({ reportType, fileName }), {
            onSuccess: () => {
                setSpinnerOpen(false);
                handleSnackBar('File Successfully Downloaded', 'success');

                sendEventWithDimensions({
                    category: 'Export',
                    action: 'Excel',
                    label: `${title} ${subTitle}`,
                });
            },
            onError: (error) => {
                setSpinnerOpen(false);
                const message = arrayBufferToString(error?.response?.data);
                DEFAULT_ERROR({ error, message, handleError: handleSnackBar });
            },
        });
    };

    const tabsArrData = generateTabs({
        data: api.getStartDateChanges.data,
    });

    const activeData = useMemo(() => {
        if (api.getStartDateChanges.data && api.getStartDateChanges.data.periodItemsList) {
            const periodItemsList = api.getStartDateChanges.data.periodItemsList;
            return (
                periodItemsList.find((item) => item.id === ui.state.activeTab)?.itemsList ||
                periodItemsList[0].itemsList
            );
        }
        return [];
    }, [api.getStartDateChanges.data, ui.state.activeTab]);

    const filteredData = useMemo(() => {
        const status = ui.state.statusOptions;
        const criticalPath = ui.state.isCriticalPath;
        const statusArray = status?.map((item) => item.value);

        if (activeData) {
            return activeData
                .filter((item) => statusArray?.includes(item.latestVersion.cardBoardViewResponse.status.name))
                .filter((item) =>
                    criticalPath
                        ? item.latestVersion.cardBoardViewResponse.cardIndicators.isCriticalPath.isIndicatorOn ===
                          criticalPath
                        : item,
                );
        }
        return null;
    }, [activeData, ui.state.isCriticalPath, ui.state.statusOptions, table.sorting]);

    useEffect(() => {
        table.setPagination((prev) => ({ ...prev, size: finalPageSize }));
    }, [finalPageSize]);

    return {
        logic: {
            tabsArrData,
            filteredData,
            spinnerOpen,
            handleAPIExcelExport,
            pageSize,
            setPageSize,
        },
    };
};
